import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  computed
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { toSignal } from '@angular/core/rxjs-interop';
import { startWith, map } from 'rxjs';
import { Policy, PolicyService, policyAutoName, policyAutoDescription, constraintLabel } from '@htx/policies';

@Component({
  selector: 'app-policy-picker-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIcon,
    MatButton,
    TranslocoPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './policy-picker-dialog.component.html',
  styleUrl: './policy-picker-dialog.component.scss'
})
export class PolicyPickerDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<PolicyPickerDialogComponent>);
  private readonly policyService = inject(PolicyService);
  private readonly transloco = inject(TranslocoService);

  readonly searchCtrl = new FormControl('');
  readonly selectedId = signal<string | null>(null);

  private readonly allPolicies = toSignal(this.policyService.getPolicies$(), { initialValue: [] });

  private readonly searchTerm = toSignal(
    this.searchCtrl.valueChanges.pipe(
      startWith(''),
      map(v => (v ?? '').toLowerCase().trim())
    ),
    { initialValue: '' }
  );

  readonly filteredPolicies = computed(() => {
    const term = this.searchTerm();
    const all = this.allPolicies();
    if (!term) return all;
    return all.filter(p => {
      const name = (p.privateProperties?.name ?? policyAutoName(this.transloco, p)).toLowerCase();
      const desc = (p.privateProperties?.description ?? '').toLowerCase();
      const id = (p.id ?? '').toLowerCase();
      return name.includes(term) || desc.includes(term) || id.includes(term);
    });
  });

  readonly isEmpty = computed(() => this.allPolicies().length === 0);

  getPolicyName(p: Policy): string {
    return p.privateProperties?.name || policyAutoName(this.transloco, p);
  }

  getPolicyDescription(p: Policy): string {
    return p.privateProperties?.description || policyAutoDescription(this.transloco, p);
  }

  getConstraintSummary(p: Policy): string {
    if (!p.constraints?.length) return '';
    return p.constraints
      .map(c => constraintLabel(this.transloco, c))
      .filter(Boolean)
      .join(' + ');
  }

  select(policy: Policy): void {
    this.dialogRef.close(policy);
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }
}
